import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import ArticleIcon from "@mui/icons-material/Article";
import { useTheme } from "@mui/material/styles";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { DataTable, StatusBadge } from "../shared";
import { SessionsChart, PageViewsChart } from "../shared/Charts";
import { toast } from "react-toastify";

const Dashboard = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [datalist, setDataLIST] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [usersData, setUsersData] = useState([]);
  const [orgsViewsData, setOrgsViewsData] = useState([]);
  const token = props?.props?.profile?.token;

  const fetchStats = async () => {
    if (!token) return;
    setAnalyticsLoading(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    
    try {
      // Fetch analytics data from new endpoint
      const analyticsResponse = await ApiServices.GetApiCall(ApiEndpoint.ADMIN_ANALYTICS_DASHBOARD, headers);
      
      if (analyticsResponse?.status && analyticsResponse.data) {
        const data = analyticsResponse.data;
        setAnalytics(data);
        
        // Set user growth chart data (last 30 days)
        if (data.user_growth_chart && Array.isArray(data.user_growth_chart)) {
          setUsersData(data.user_growth_chart);
        }
        
        // Set organization distribution chart data from API
        // Use users_by_role for better visualization - shows user distribution by role (excluding organizations)
        if (data.users_by_role && Array.isArray(data.users_by_role)) {
          const roleChartData = data.users_by_role
            .filter(item => item.role !== 'schools' && item.role !== 'master_org' && item.role !== 'sub_org') // Exclude organizations
            .map(item => ({
              month: item.role === 'player' ? 'Players' :
                     item.role === 'coach' ? 'Coaches' :
                     item.role === 'referee' ? 'Referees' :
                     item.role === 'parent' ? 'Parents' :
                     item.role?.charAt(0).toUpperCase() + item.role?.slice(1) || 'Unknown',
              views: item.count || 0,
              followers: 0, // Not applicable for user roles
            }));
          setOrgsViewsData(roleChartData);
        } else if (data.organization_distribution && Array.isArray(data.organization_distribution)) {
          // Fallback to organization distribution if users_by_role not available
          const orgChartData = data.organization_distribution.map(org => ({
            month: org.name || org.type || 'Unknown',
            views: org.value || org.views || 0,
            followers: 0, // API doesn't provide followers separately
          }));
          setOrgsViewsData(orgChartData);
        }
      } else {
        // Fallback if analytics endpoint fails
        console.warn('Analytics endpoint returned no data, using fallback');
        setUsersData([]);
        setOrgsViewsData([]);
      }
    } catch (e) {
      console.error('Failed to load analytics:', e);
      toast.error("Failed to load dashboard data");
      setUsersData([]);
      setOrgsViewsData([]);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    setLoading(true);
    const body = {
      type: "signup",
      page: page + 1,
      limit: rowsPerPage,
      pagination: true,
    };
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_USER_LIST,
        JSON.stringify(body),
        headers
      );
      setLoading(false);
      if (data?.status) {
        let allData = data.data || [];
        
        // Filter out pending organizations - only show active ones
        // Organizations have role "schools" and should be filtered by status
        const filteredData = allData.filter(item => {
          // If it's an organization (role === "schools")
          if (item.role === "schools" || item.role === "school") {
            // Only include if status is "active"
            const status = item.status || item.connection_status || "active";
            return status === "active";
          }
          // Include all non-organization items (users)
          return true;
        });
        
        setDataLIST(filteredData);
        setTotalCount(filteredData.length);
      }
    } catch (e) {
      setLoading(false);
      toast.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    if (token) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, rowsPerPage]);

  // Metric Card Component (matching MUI template style with pop-out effect)
  const MetricCard = ({ title, value, change, trend, icon: Icon, color = "primary" }) => {
    const isPositive = trend === "up";
    const changeColor = isPositive ? "#2563eb" : "#ef4444";

    return (
      <Card
        sx={{
          height: "100%",
          backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          boxShadow: isDark
            ? "0 4px 12px rgba(0, 0, 0, 0.5)"
            : "0 4px 12px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: isDark
              ? "0 8px 24px rgba(99, 102, 241, 0.3)"
              : "0 8px 24px rgba(25, 118, 210, 0.2)",
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "0.8125rem" : "0.875rem",
                  fontWeight: 500,
                  color: isDark ? "#cbd5e1" : "#718096",
                  mb: 1,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  fontWeight: 700,
                  color: isDark ? "#ffffff" : "#1a202c",
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
            </Box>
            <Avatar
              sx={{
                backgroundColor: isDark
                  ? "rgba(99, 102, 241, 0.15)"
                  : color === "primary"
                  ? "rgba(25, 118, 210, 0.1)"
                  : "#f7fafc",
                color: isDark ? "#818cf8" : color === "primary" ? "#1976d2" : "#4a5568",
                width: isMobile ? 44 : 56,
                height: isMobile ? 44 : 56,
                boxShadow: isDark
                  ? "0 4px 12px rgba(99, 102, 241, 0.2)"
                  : "0 4px 12px rgba(25, 118, 210, 0.15)",
              }}
            >
              <Icon sx={{ fontSize: isMobile ? 22 : 28 }} />
            </Avatar>
          </Box>
          {change && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isPositive ? (
                <TrendingUpIcon sx={{ fontSize: 16, color: changeColor }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, color: changeColor }} />
              )}
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: changeColor,
                }}
              >
                {change}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: isDark ? "#718096" : "#cbd5e1",
                  ml: 0.5,
                }}
              >
                vs last month
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 80,
    },
    {
      field: "name",
      label: "User",
      render: (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={row.profile_image}
            sx={{
              width: 40,
              height: 40,
              bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {row.first_name?.[0]}
            {row.last_name?.[0]}
          </Avatar>
          <Box>
            <Box sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {row.first_name} {row.last_name}
            </Box>
            <Box sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.email}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "role",
      label: "Role",
      mobileSameLineGroup: "meta",
      render: (value) => (
        <Box
          sx={{
            textTransform: "capitalize",
            color: isDark ? "#cbd5e1" : "#4a5568",
            fontSize: "0.875rem",
          }}
        >
          {value === "referee"
            ? "Referee"
            : value === "schools"
            ? "Club"
            : value || "-"}
        </Box>
      ),
    },
    {
      field: "status",
      label: "Status",
      align: "right",
      mobileSameLineGroup: "meta",
      render: (value) => <StatusBadge status={value} />,
    },
  ];

  const renderActions = null; // Remove actions column from Details section

  // Extract analytics data with defaults - API returns: users, organizations, posts
  const userMetrics = analytics?.users || {};
  const orgMetrics = analytics?.organizations || {};
  const postMetrics = analytics?.posts || {};
  const subOrgMetrics = orgMetrics?.sub_orgs || {};
  
  // Map API structure: { total, active, growth_percent, this_month, last_month, recent_7_days }
  const totalUsers = userMetrics?.total || 0;
  const activeUsers = userMetrics?.active || 0;
  const userGrowthPercent = userMetrics?.growth_percent || 0;
  const recent7Days = userMetrics?.recent_7_days || 0;
  
  // Map API structure: { total, paid, master, sub_orgs: { total, growth_percent, this_month, last_month } }
  const totalOrgs = orgMetrics?.total || 0;
  const paidOrgs = orgMetrics?.paid || 0;
  const masterOrgs = orgMetrics?.master || 0;
  const approvedSubOrgs = subOrgMetrics?.total || 0;
  const subOrgGrowthPercent = subOrgMetrics?.growth_percent || 0;
  
  // Map API structure: { total, active }
  const totalPosts = postMetrics?.total || 0;
  const activePosts = postMetrics?.active || 0;
  const inactivePosts = totalPosts - activePosts;
  const activePostsPercent = totalPosts > 0 ? Math.round((activePosts / totalPosts) * 100) : 0;
  const inactivePostsPercent = 100 - activePostsPercent;

  // Calculate totals from API data only
  const totalUsersCount = analytics?.total_users_30_days !== undefined 
    ? analytics.total_users_30_days 
    : (usersData.length > 0 ? usersData.reduce((sum, d) => sum + (d.users || 0), 0) : 0);
  const totalOrgsViews = orgsViewsData.length > 0 
    ? orgsViewsData.reduce((sum, d) => sum + (d.views || 0), 0) 
    : 0;

  return (
    <Box sx={{ pb: { xs: 2, md: 3 } }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography
          sx={{
            fontSize: isMobile ? "0.8125rem" : "0.875rem",
            color: isDark ? "#718096" : "#718096",
            mb: 0.5,
          }}
        >
          Dashboard &gt; Home
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontSize: isMobile ? "1.25rem" : "1.5rem",
            fontWeight: 700,
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        >
          Overview
        </Typography>
      </Box>

      {/* Metrics Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 }, alignItems: "stretch" }}>
        {analyticsLoading ? (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <Skeleton variant="rectangular" height={isMobile ? 100 : 140} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Skeleton variant="rectangular" height={isMobile ? 100 : 140} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Skeleton variant="rectangular" height={isMobile ? 100 : 140} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Skeleton variant="rectangular" height={isMobile ? 100 : 140} sx={{ borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Skeleton variant="rectangular" height={isMobile ? 260 : 400} sx={{ borderRadius: 2 }} />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Total Users"
                value={totalUsers.toLocaleString()}
                change={userGrowthPercent > 0 ? `+${userGrowthPercent.toFixed(1)}%` : `${userGrowthPercent.toFixed(1)}%`}
                trend={userGrowthPercent >= 0 ? "up" : "down"}
                icon={GroupIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Active Users"
                value={activeUsers.toLocaleString()}
                change={`${recent7Days} last week`}
                trend="up"
                icon={CheckCircleIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Total Clubs"
                value={totalOrgs.toLocaleString()}
                change={`${paidOrgs} paid, ${masterOrgs} master`}
                trend="up"
                icon={BusinessIcon}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Approved Clubs"
                value={approvedSubOrgs}
                change={subOrgGrowthPercent > 0 ? `+${subOrgGrowthPercent.toFixed(1)}%` : `${subOrgGrowthPercent.toFixed(1)}%`}
                trend={subOrgGrowthPercent >= 0 ? "up" : "down"}
                icon={CheckCircleIcon}
                color="secondary"
              />
            </Grid>
            {/* Total Posts and Inactive Posts stacked vertically */}
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 3 }, width: "100%", height: "100%" }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <MetricCard
                    title="Total Posts"
                    value={totalPosts.toLocaleString()}
                    change={`${activePosts} active`}
                    trend="up"
                    icon={AssignmentIcon}
                    color="warning"
                  />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <MetricCard
                    title="Inactive Posts"
                    value={inactivePosts.toLocaleString()}
                    change={`${inactivePostsPercent.toFixed(0)}% of total`}
                    trend={inactivePosts > 0 ? "down" : "up"}
                    icon={ArticleIcon}
                    color="error"
                  />
                </Box>
              </Box>
            </Grid>
            
            {/* Views and Followers Chart - moved here next to Total Posts */}
            <Grid item xs={12} md={9}>
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
                  boxShadow: isDark
                    ? "0 4px 12px rgba(0, 0, 0, 0.5)"
                    : "0 4px 12px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: isMobile ? "1rem" : "1.125rem",
                          fontWeight: 600,
                          color: isDark ? "#ffffff" : "#1a202c",
                          mb: 0.5,
                        }}
                      >
                        Users by Role
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: isMobile ? "0.8rem" : "0.875rem",
                          color: isDark ? "#cbd5e1" : "#718096",
                        }}
                      >
                        Distribution of users across different roles
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: isMobile ? "left" : "right", flexShrink: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a202c",
                        }}
                      >
                        {totalOrgsViews > 0 ? totalOrgsViews : totalUsers}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: isDark ? "#9e9e9e" : "#757575",
                          mt: 0.5,
                        }}
                      >
                        Total users
                      </Typography>
                    </Box>
                  </Box>
                  <PageViewsChart data={orgsViewsData} />
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Users Chart Card (Changed from Sessions) */}
      {analyticsLoading ? (
        <Skeleton variant="rectangular" height={isMobile ? 280 : 400} sx={{ borderRadius: 2, mb: { xs: 2, md: 3 } }} />
      ) : (
      <Card
        sx={{
          mb: { xs: 2, md: 3 },
          backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          boxShadow: isDark
            ? "0 4px 12px rgba(0, 0, 0, 0.5)"
            : "0 4px 12px rgba(0, 0, 0, 0.08)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 1, mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "1rem" : "1.125rem",
                  fontWeight: 600,
                  color: isDark ? "#ffffff" : "#1a202c",
                  mb: 0.5,
                }}
              >
                Users
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "0.8rem" : "0.875rem",
                  color: isDark ? "#cbd5e1" : "#718096",
                }}
              >
                New users per day for the last 30 days
              </Typography>
            </Box>
            <Box sx={{ textAlign: isMobile ? "left" : "right", flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: isDark ? "#ffffff" : "#1a202c",
                }}
              >
                {totalUsersCount.toLocaleString()}
              </Typography>
              {userGrowthPercent !== undefined && userGrowthPercent !== null && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                  {userGrowthPercent >= 0 ? (
                <TrendingUpIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                  ) : (
                <TrendingDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                  )}
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                      color: userGrowthPercent >= 0 ? "#2563eb" : "#ef4444",
                  }}
                >
                    {userGrowthPercent > 0 ? '+' : ''}{userGrowthPercent.toFixed(1)}%
                </Typography>
              </Box>
              )}
            </Box>
          </Box>
          <SessionsChart data={usersData.length > 0 ? usersData.map(d => ({ date: d.date, users: d.users || 0 })) : []} />
        </CardContent>
      </Card>
      )}

      {/* Details Section */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: isMobile ? "1rem" : "1.125rem",
            fontWeight: 600,
            color: isDark ? "#ffffff" : "#1a202c",
            mb: 2,
          }}
        >
          Details
        </Typography>
      </Box>

      {/* Recent Users Table */}
      <Card
        sx={{
          backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          boxShadow: isDark
            ? "0 4px 12px rgba(0, 0, 0, 0.5)"
            : "0 4px 12px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={datalist}
            loading={loading}
            actions={renderActions}
            pagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            onRowClick={(row) => {
              // Open user details modal instead of navigating
              // This will be handled by parent component
            }}
            emptyMessage="No recent users found"
            mobileTitleColumn="name"
            mobileHiddenColumns={["id"]}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default Dashboard;
